import ToolbarButton from "../toolbar/ToolbarButton";
import { useEffect, useState, useContext } from "react";
import { useStrategy } from "../../api/strategy";
import * as geojson from 'geojson';
import FuelLoadLayerPopover from "../FuelLoadLayerPopover";
import {
  FUEL_FILL_COLOR,
  FUEL_FILL_OPACITY,
} from "../../constants/fuelLoadPaint";
import { StrategyContext } from "../../contexts/StrategyContext";
import { useRetainedTilesUrl } from "../../hooks/useRetainedTilesUrl";
import { recalcGreyed } from "../../constants/recalcPaint";
import { mapEventScreenPoint } from "../../utils/uiZoom";

// Type definitions
type LayerType = "none" | "fuelLoad";

// Custom hook for managing layer data and settings
const useLayerData = (selectedStrategy: any) => {
  const { strategy } = useStrategy();
  const [active, setActive] = useState(false);
  const [stale, setStale] = useState(false);
  const strategyData = selectedStrategy
    ? strategy?.data?.[selectedStrategy.id] ?? null
    : null;

  // The live URL disappears while a fuel recalculation is in flight (any
  // mutation bumps strategyRefreshToken, resetting the strategyForYear query).
  // Keep the user's toggle state and the retained tiles through that window —
  // the layer renders them greyed out (recalculating) instead of hiding.
  const liveTilesUrl = strategyData?.tiles_url as string | undefined;
  const { tilesUrl, isRetained: recalculating } =
    useRetainedTilesUrl(liveTilesUrl);
  // Retained tiles are still loadable, so the toggle stays usable while the
  // recalculation runs — only a strategy with no tiles at all disables it.
  const dataAvailable = Boolean(tilesUrl);

  return {
    dataAvailable,
    active,
    setActive,
    stale,
    setStale,
    tilesUrl,
    recalculating,
  };
};

// Main component
const FuelLoadLayer = (
  id: string,
  name: string,
) => {
  const { selectedStrategy } = useContext(StrategyContext);
  const [selectedFeature, setSelectedFeature] = useState<geojson.Feature | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  
  // Use the custom hooks
  const {
    dataAvailable,
    active,
    stale,
    setStale,
    setActive,
    tilesUrl,
    recalculating,
  } = useLayerData(selectedStrategy);

  const handleToggle = () => {
    const nextActive = !active;
    setActive(nextActive);
    setStale(true);
    if (!nextActive) {
      setSelectedFeature(null);
      setPopoverPosition(null);
    }
  };

  useEffect(() => {
    if (tilesUrl && active) {
      setStale(true);
    }
  }, [tilesUrl, active, setStale]);

  // Re-apply paint when the greyed (recalculating) state flips, and drop any
  // open popover — its values belong to the stale data.
  useEffect(() => {
    if (!active) return;
    setStale(true);
    if (recalculating) {
      setSelectedFeature(null);
      setPopoverPosition(null);
    }
  }, [recalculating, active, setStale]);

  const layerType: LayerType = active ? "fuelLoad" : "none";


  const isVector = active && !!selectedStrategy && !!tilesUrl;
  const vectorSource = isVector ? {
    tiles: [tilesUrl],
    minzoom: 6,
    // Tileset is generated to z12 (see PMTILES_MAX_ZOOM); declaring the real
    // max lets MapLibre overzoom z12 tiles instead of requesting non-existent
    // z13/z14 tiles (which render blank until a pan).
    maxzoom: 12,
    tileSize: 512,
    promoteId: "__id",
  } : null;

  const layerStyle = {
    id: id,
    type: "fill",
    "source-layer": "fuel_load",
    paint: {
      // While recalculating, pull the ramp toward grey but keep each cell's
      // own colour showing through so the polygons stay distinguishable.
      "fill-color": recalculating
        ? recalcGreyed(FUEL_FILL_COLOR)
        : FUEL_FILL_COLOR,
      "fill-opacity": FUEL_FILL_OPACITY,
    },
    layout: {
      visibility: active ? "visible" : "none",
    },
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={dataAvailable}
      onClick={handleToggle}
      icon={"scale"}
    />
  );

  const parseNumericRecord = (value: unknown): Record<string, number> | null => {
    if (value === undefined || value === null) return null;

    let rawObject: Record<string, unknown> | null = null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      try {
        rawObject = JSON.parse(trimmed);
      } catch (error) {
        console.warn('Failed to parse fuel-load JSON payload', error);
        return null;
      }
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      rawObject = value as Record<string, unknown>;
    }

    if (!rawObject) return null;

    const result: Record<string, number> = {};
    Object.entries(rawObject).forEach(([key, raw]) => {
      const numeric = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isNaN(numeric)) {
        result[key] = numeric;
      }
    });

    return Object.keys(result).length > 0 ? result : null;
  };

  const toNumber = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    if (typeof value === 'string') {
      const numeric = Number(value);
      return Number.isNaN(numeric) ? null : numeric;
    }
    return null;
  };

  const handleFeatureClick = (e: any) => {
    // Only handle clicks if the layer is active
    if (!active) return;

    const features = e.features;
    if (features && features.length > 0) {
      const feature = features[0];

      const properties: Record<string, unknown> = {
        ...(feature.properties || {}),
        layerType,
      };

      if (layerType === 'fuelLoad') {
        const strataBreakdown = parseNumericRecord(
          (feature.properties as any)?.strataBreakdown ?? feature.properties?.strata_breakdown);

        const characteristicValues = parseNumericRecord(
          (feature.properties as any)?.fuelCharacteristics ?? feature.properties?.characteristics);

        const fuelLoadValue = toNumber(feature.properties?.fuel_load);

        if (strataBreakdown) {
          properties.strataBreakdown = strataBreakdown;
        }
        if (characteristicValues) {
          properties.fuelCharacteristics = characteristicValues;
        }
        if (fuelLoadValue !== null) {
          properties.fuelLoad = fuelLoadValue;
        }
      }

      const featureWithDetails = {
        ...feature,
        properties: {
          ...properties,
        },
      };
      setSelectedFeature(featureWithDetails as geojson.Feature);
      setPopoverPosition(mapEventScreenPoint(e));
    }
  };

  const handleClosePopover = () => {
    setSelectedFeature(null);
    setPopoverPosition(null);
  };

  // No clicks while greyed — the popover would report stale values.
  const interactiveLayerIds = active && !recalculating ? [id] : [];

  return {
    id: id,
    name: name,
    type: "vector",
    source: vectorSource,
    rawSource: vectorSource,
    layerStyle: [layerStyle],
    toolbarButton: toolbarButton,
    active: active,
    stale: stale,
    setStale: setStale,
    onClick: handleFeatureClick,
    interactiveLayerIds: interactiveLayerIds,
    popover: (
      <FuelLoadLayerPopover
        feature={selectedFeature}
        anchorPosition={popoverPosition}
        onClose={handleClosePopover}
      />
    )
  };
};

export default FuelLoadLayer;